import { z } from 'zod';

/**
 * Schemas for the per-project Room grouping.
 *
 * Path: `/projects/:projectId/rooms`
 *
 * ProjectRoom is a grouping mechanism for sub-items (cabinets, panels, etc.)
 * within a project. Each room has a name, optional description, and orderIndex
 * for sorting in the UI.
 */

const optionalTrim = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .transform((v) => (v === '' ? undefined : v));

export const roomListItem = z.object({
  id: z.string().cuid(),
  projectId: z.string().cuid(),
  name: z.string(),
  description: z.string().nullable(),
  orderIndex: z.number().int(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type RoomListItem = z.infer<typeof roomListItem>;

export const createRoomRequest = z.object({
  name: z.string().trim().min(1).max(200),
  description: optionalTrim(2000),
  orderIndex: z.number().int().min(0).optional(),
});
export type CreateRoomRequest = z.infer<typeof createRoomRequest>;

export const updateRoomRequest = z
  .object({
    name: z.string().trim().min(1).max(200).optional(),
    description: optionalTrim(2000),
    orderIndex: z.number().int().min(0).optional(),
  })
  .refine(
    (v) => v.name !== undefined || v.description !== undefined || v.orderIndex !== undefined,
    { message: 'At least one field is required.' },
  );
export type UpdateRoomRequest = z.infer<typeof updateRoomRequest>;

export const listRoomsResponse = z.array(roomListItem);
export type ListRoomsResponse = z.infer<typeof listRoomsResponse>;

/**
 * BOM offer exclusions.
 *
 * Each key identifies a BOM row (catalog reference) as `"<entityType>:<entityId>"`
 * — the same identity used by the room BOM (`ProjectBomReport.groups[].entityType`
 * + `entityId`). A key present in the set means that catalog reference is
 * *excluded* from the room's detailed offer PDF: any line item consuming it is
 * dropped. The regular (summary) PDF ignores exclusions entirely.
 *
 * Path: `/projects/:projectId/rooms/:id/bom-exclusions`
 */
export const bomEntityKey = z
  .string()
  .regex(
    /^(material|edge|hardware|lighting|drawerVariant):.+$/,
    'Expected "<entityType>:<entityId>"',
  );

export const bomExclusionsResponse = z.object({
  excludedKeys: z.array(bomEntityKey),
  includedKeys: z.array(bomEntityKey),
});
export type BomExclusionsResponse = z.infer<typeof bomExclusionsResponse>;

export const setBomExclusionsRequest = z.object({
  excludedKeys: z.array(bomEntityKey).max(1000),
  includedKeys: z.array(bomEntityKey).max(1000).default([]),
});
export type SetBomExclusionsRequest = z.infer<typeof setBomExclusionsRequest>;

// ---------------------------------------------------------------------------
// Detailed-offer preselection rule (which BOM rows are checked by default)
// ---------------------------------------------------------------------------

/** BOM entity types — mirrors `ProjectBomReport.groups[].entityType`. */
export type BomEntityType = 'material' | 'edge' | 'hardware' | 'lighting' | 'drawerVariant';

/**
 * Unit-price threshold (RON) for the default preselection: hardware and lighting
 * BOM rows are checked by default only when their catalog unit price is strictly
 * above this. Everything at/below (and rows with no catalog price) start unchecked.
 */
export const BOM_UNIT_PRICE_THRESHOLD = 10;

/**
 * Default detailed-offer selection for a BOM row, before any manual override:
 * - material, drawerVariant → always selected (important)
 * - edge → never selected (unimportant)
 * - hardware, lighting → selected only when unit price > BOM_UNIT_PRICE_THRESHOLD
 *   (a missing catalog price counts as below the threshold → unselected)
 */
export function bomDefaultSelected(
  entityType: BomEntityType,
  unitPrice: number | string | null | undefined,
): boolean {
  switch (entityType) {
    case 'material':
    case 'drawerVariant':
      return true;
    case 'edge':
      return false;
    case 'hardware':
    case 'lighting': {
      const price = unitPrice == null ? NaN : Number(unitPrice);
      return Number.isFinite(price) && price > BOM_UNIT_PRICE_THRESHOLD;
    }
    default:
      return true;
  }
}

/** A room's manual detailed-offer overrides: keys forced on/off; the rest follow the rule. */
export interface BomSelectionOverrides {
  include: ReadonlySet<string>;
  exclude: ReadonlySet<string>;
}

/**
 * Effective detailed-offer selection for a BOM row: an explicit manual override
 * wins (include beats exclude), otherwise {@link bomDefaultSelected} decides.
 */
export function bomRowSelected(
  key: string,
  entityType: BomEntityType,
  unitPrice: number | string | null | undefined,
  overrides: BomSelectionOverrides,
): boolean {
  if (overrides.include.has(key)) return true;
  if (overrides.exclude.has(key)) return false;
  return bomDefaultSelected(entityType, unitPrice);
}

/**
 * Parse a room's stored detailed-offer overrides. Two shapes are accepted for
 * backward compatibility:
 *  - legacy: a bare array of excluded keys (everything else was included)
 *  - current: `{ exclude: string[], include: string[] }`
 */
export function parseBomSelection(value: unknown): {
  excludedKeys: string[];
  includedKeys: string[];
} {
  const strings = (v: unknown): string[] =>
    Array.isArray(v) ? v.filter((k): k is string => typeof k === 'string') : [];
  if (Array.isArray(value)) return { excludedKeys: strings(value), includedKeys: [] };
  if (value && typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    return { excludedKeys: strings(obj.exclude), includedKeys: strings(obj.include) };
  }
  return { excludedKeys: [], includedKeys: [] };
}
